import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  CHECK_LIST,
  TEXT_MATCH_TRANSFORMERS,
  type Transformer,
  UNORDERED_LIST,
} from "@lexical/markdown";
import * as Sentry from "@sentry/vue";
import htmlToPdfmake from "html-to-pdfmake";
import { $getRoot, createEditor, ElementNode, type Klass, type LexicalNode, type SerializedEditorState } from "lexical";
import { marked } from "marked";
import pdfMake from "pdfmake/build/pdfmake";
import type { TDocumentDefinitions } from "pdfmake/interfaces";

import { PropertyKind } from "~/shared/enums";
import type { Doc, Page, Property, PropertyConfig, Task } from "~/shared/types";
import { saveFile } from "~/utils/api";
import { isImageType } from "~/utils/media";

import {
  BOLD_STAR_SINGLE,
  CODE_BACKTICK,
  CODE_BLOCK,
  HEADING,
  HIGHLIGHT_EQUALS,
  ITALICS_HYPHEN,
  ORDERED_LIST,
  QUOTE,
  STRIKE_THROUGH_TILDE,
  UNDERLINE_UNDERSCORE,
} from "./markdownRules";
import { HORIZONTAL_RULE_TRANSFORMER } from "./nodes/HorizontalRuleNode";

type GetShownPropertyWithConfigList = (
  alwaysIncludeKinds?: PropertyKind[],
  alwaysIncludeDuids?: string[]
) => [Property, PropertyConfig][];

// code should go first as it prevents any transformations inside
// then longer tags match (e.g. ** or __ should go before * or _)
export const TRANSFORMERS: Transformer[] = [
  CODE_BLOCK,
  // element
  HEADING,
  QUOTE,
  CHECK_LIST,
  ORDERED_LIST,
  UNORDERED_LIST,
  // TODO toggle block add back
  // TOGGLE_BLOCK_TRANSFORMER,
  HORIZONTAL_RULE_TRANSFORMER,
  // text format
  BOLD_STAR_SINGLE,
  ITALICS_HYPHEN,
  UNDERLINE_UNDERSCORE,
  STRIKE_THROUGH_TILDE,
  CODE_BACKTICK,
  HIGHLIGHT_EQUALS,
  // link only
  ...TEXT_MATCH_TRANSFORMERS,
];

let FULL_DART_EDITOR_NODES: Klass<LexicalNode>[] = [];

export const init = (fullDartEditorNodes: Klass<LexicalNode>[]) => {
  FULL_DART_EDITOR_NODES = fullDartEditorNodes;
};

export const fillNodeWithMarkdown = (node: ElementNode, markdown: string) => {
  $convertFromMarkdownString(markdown, TRANSFORMERS, node);
};

const escapeCsvField = (field: string) => {
  let res = field.replaceAll('"', '""');
  if (res.includes(",") || res.includes("\n") || res.includes('"')) {
    res = `"${res}"`;
  }
  return res;
};

const escapeMarkdownField = (field: string) => field.replace(/([\\|])/g, "\\$1").replace(/\s/g, " ");

export const convertTableToCsv = (table: string[][]) =>
  table.map((row) => row.map(escapeCsvField).join(",")).join("\n");

export const convertTableToMarkdown = (table: string[][]) => {
  if (table.length === 1) {
    return "No tasks";
  }
  const headers = table[0].map(escapeMarkdownField).join(" | ");
  const separator = table[0].map(() => "-").join(" | ");
  const rows = table
    .slice(1)
    .map((row) => row.map(escapeMarkdownField).join(" | "))
    .join("\n");
  return `${headers}\n${separator}\n${rows}`;
};

export const convertToMarkdown = (text: SerializedEditorState) => {
  const editor = createEditor({ nodes: FULL_DART_EDITOR_NODES });
  editor.setEditorState(editor.parseEditorState(text));

  let res = "";
  editor.update(() => {
    res = $convertToMarkdownString(TRANSFORMERS, $getRoot());
  });
  return res;
};

const formatAttachmentsSection = (attachments: { name: string; fileUrl: string; kind: string }[]) => {
  if (attachments.length === 0) {
    return "";
  }

  // TODO exposing the fact that we have security by obscurity for attachments
  const attachmentsList = attachments
    .map((attachment) => {
      if (isImageType(attachment.kind)) {
        return `<img src="${attachment.fileUrl}" alt="${attachment.name}" style="max-width: 100%; height: auto; display: inline-block;" />`;
      }
      return `- [${attachment.name}](${attachment.fileUrl})`;
    })
    .join("\n\n");

  return `\n\n---\n\n## Attachments\n\n${attachmentsList}`;
};

const formatPropertiesTable = (task: Task, getShownPropertyWithConfigList: GetShownPropertyWithConfigList) =>
  getShownPropertyWithConfigList()
    .map(([property, config]) => `| ${property.title} | ${config.getValuePretty(property, task)} |`)
    .join("\n");

export const convertTaskToMarkdown = async (
  task: Task,
  getShownPropertyWithConfigList: GetShownPropertyWithConfigList,
  dataStore: {
    getAttachmentsByDuidsOrdered: (duids: string[]) => Promise<{ name: string; fileUrl: string; kind: string }[]>;
  }
) => {
  const propertiesTable = formatPropertiesTable(task, getShownPropertyWithConfigList);
  const attachmentsSection = formatAttachmentsSection(
    await dataStore.getAttachmentsByDuidsOrdered(task.attachmentDuids)
  );
  return `# ${task.title}\n\n---\n\n| Property | Value |\n| - | - |\n${propertiesTable}\n\n---\n\n${convertToMarkdown(task.description)}${attachmentsSection}`;
};

export const convertDocToMarkdown = (doc: Doc) => `# ${doc.title}\n\n---\n\n${convertToMarkdown(doc.text)}`;

export const convertPageToTable = (tasks: Task[], getShownPropertyWithConfigList: GetShownPropertyWithConfigList) => {
  const properties = getShownPropertyWithConfigList([PropertyKind.DEFAULT_TITLE, PropertyKind.DEFAULT_DESCRIPTION]);
  const headers = properties.map(([property]) => property.title);
  const rows = tasks.map((task) => properties.map(([property, config]) => config.getValuePretty(property, task)));
  return [headers, ...rows];
};

export const convertPageToMarkdown = (
  page: Page,
  groupedTasks: { title: string; tasks: Task[] }[],
  getShownPropertyWithConfigList: GetShownPropertyWithConfigList
) => {
  const tasksStr = groupedTasks
    .map(({ title, tasks }) => {
      const table = convertPageToTable(tasks, getShownPropertyWithConfigList);
      const markdown = convertTableToMarkdown(table);
      return `## ${title}\n${markdown}`;
    })
    .join("\n\n");

  return `# ${page.title}\n\n---\n\n${tasksStr}`;
};

export const convertToHtml = (markdownStr: string) => marked.parse(markdownStr) as string;

export const convertToPlainText = (markdownStr: string) => {
  const htmlString = marked.parse(markdownStr) as string;
  const doc = new DOMParser().parseFromString(htmlString, "text/html");
  return doc.body.textContent;
};

export const saveAsPdf = async (
  markdownStr: string,
  fileSlug: string,
  options?: { postprocessHtml?: (html: string) => string; pdfOptions?: Omit<TDocumentDefinitions, "content"> }
) => {
  const { postprocessHtml, pdfOptions } = options ?? {};
  let html = convertToHtml(markdownStr);
  if (postprocessHtml) {
    html = postprocessHtml(html);
  }

  // Convert image URLs to data URLs
  const imageUrls = html.match(/src="([^"]+)"/g)?.map((src) => src.slice(5, -1)) ?? [];
  const imageDataUrls: Record<string, string> = {};

  await Promise.all(
    imageUrls.map(async (url) => {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const reader = new FileReader();
        const dataUrl = await new Promise<string>((resolve) => {
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
        imageDataUrls[url] = dataUrl;
      } catch (error) {
        Sentry.captureMessage(`Failed to load image ${url} while saving as PDF`);
      }
    })
  );

  // Replace image URLs with data URLs in HTML
  Object.entries(imageDataUrls).forEach(([url, dataUrl]) => {
    html = html.replace(new RegExp(url, "g"), dataUrl);
  });

  const content = htmlToPdfmake(html, { tableAutoSize: true });
  const pdf = pdfMake.createPdf({ content, ...pdfOptions });
  pdf.download(`${fileSlug}.pdf`);
};

export const saveAsCsv = (csvStr: string, fileSlug: string) => {
  saveFile(new Blob([csvStr], { type: "text/csv" }), `${fileSlug}.csv`);
};

export const saveAsHtml = (
  markdownStr: string,
  fileSlug: string,
  options?: { postprocessHtml?: (html: string) => string }
) => {
  const { postprocessHtml } = options ?? {};
  let html = convertToHtml(markdownStr);
  if (postprocessHtml) {
    html = postprocessHtml(html);
  }
  saveFile(new Blob([html], { type: "text/plain" }), `${fileSlug}.html`);
};

export const saveAsMarkdown = (markdownStr: string, fileSlug: string) => {
  saveFile(new Blob([markdownStr], { type: "text/plain" }), `${fileSlug}.md`);
};

export const getExportNewWindowScriptContent = (x: string, print: boolean, width: number, height: number) => {
  if (print) {
    return `<script>
const run = async () => {
  window.print();
  window.close();
};

window.onload = run;
</script>`;
  }

  return `<script src="https://cdnjs.cloudflare.com/ajax/libs/html-to-image/1.11.13/html-to-image.min.js" integrity="sha512-iZ2ORl595Wx6miw+GuadDet4WQbdSWS3JLMoNfY8cRGoEFy6oT3G9IbcrBeL6AfkgpA51ETt/faX6yLV+/gFJg==" crossorigin="anonymous" referrerpolicy="no-referrer"></script>
<script>
const run = async () => {
  const content = document.body.children[0];

  const dataUrl = await htmlToImage.toPng(content, {
    width: ${width},
    height: ${height},
  });
  const link = document.createElement("a");
  link.download = "${x}.png";
  link.href = dataUrl;
  link.click();
  link.remove();

  document.getElementsByClassName("dart-message")[0].innerHTML = "After the file downloads, close this window";
};

window.onload = run;
</script>`;
};
